import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import { getOrgPolicyV1, getPoliciesByGroupIdsV1 } from '@/lib/services/policy-v1.service';
import { getEndUserById, deleteUser, createUser, patchUser } from '@/lib/services/user.service';
import Drawer from '../';
import DrawerContent from '../content';
import DrawerHeader from '../header';
import { UserModel } from '@/lib/models/user.model';
import { GroupMeta } from '@/lib/models/group.model';
import { getNonSyncGroupOptions } from '@/lib/services/group.service';
import { QueryKey, refetchQuery } from '@/lib/query-client';
import RouterTabs from '@/components/router-tabs';
import UserDetails from './user-details';
import UserPolicies from './user-policies';
import UserInsights from './insights';
import useMetabase from '@/hooks/use-metabase';
import UserTimeline from './timeline';
import { TabPanel } from '@/components/router-tabs/helpers';
import UserLink from '@/components/user-link';
import { DrawerProps } from '../helpers';
import useViewMode from '@/hooks/use-view-mode.hook';
import MoreMenu from '@/components/more-menu';
import UpdateMenuItems from '@/components/more-menu/update-menu-items';
import useToast from '@/hooks/use-toast.hook';
import useModal from '@/hooks/use-modal.hook';
import useFeature from '@/hooks/use-feature.hook';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { Form } from '@/components/form';
import InputControl from '@/components/form/input-control';
import DrawerButtons from '@/components/form/drawer-buttons';
import { FieldError } from '@/lib/services';

interface Props extends DrawerProps {
  id: string;
}

type UserQueryResult = [UserModel | null, PolicyModelV1[], GroupMeta[]];

const formModel = new UserModel();

export const UserDrawer: FC<Props> = ({
  id,
  open,
  onClose,
  className,
  isTopDrawer,
  hideUpdateMenu,
}) => {
  const [userId, setUserId] = useState(id || null);
  const isNew = !userId;
  const { dashboards } = useMetabase();
  const { isValid: isDashboardValid } = dashboards.userGroupLg;
  const { toast, errorToast } = useToast();
  const { openModal } = useModal();
  const { canChangeUsers } = useFeature();
  const { setMode, inWriteMode } = useViewMode(isNew ? 'add' : 'view');

  const handleRefresh = () => {
    refetchQuery(QueryKey.UsersDataset);
  };

  const query = useQuery<UserQueryResult>(
    [QueryKey.UserMetaView, userId],
    async () => {
      const [userData, orgPolicy] = await Promise.all([getEndUserById(userId!), getOrgPolicyV1()]);

      const [user, groupMeta] = userData;

      if (!user) {
        return [null, [], []];
      }

      let policies: PolicyModelV1[] = [];

      if (orgPolicy) {
        policies.push(orgPolicy);
      }

      if (groupMeta.length) {
        const groupIds = groupMeta.map(({ id }) => id);
        const groupPolicies = await getPoliciesByGroupIdsV1(groupIds);
        policies = [...policies, ...groupPolicies];
      }

      const groups = Array.from(groupMeta.entries()).map(([, group]) => group);

      return [user, policies, groups];
    },
    {
      enabled: !isNew,
    }
  );

  const { data: nonSyncGroupOpts } = useQuery([QueryKey.GroupsNonSyncOptions], async () =>
    getNonSyncGroupOptions()
  );

  const { data, refetch } = query;
  const [userData, policies = [], groups = []] = data || [];
  const user = userData || new UserModel();

  const { name: userName, isExternal, canDelete, isConnectorUser, hasLogin } = user;

  const canEditProfile = !isExternal && !user.hasLogin;

  const formHandle = useFormHandle(
    {
      initialValues: formModel,
      validationSchema: formModel.schema,
      validateOnChange: !isNew,
      onSubmit: async (values, { resetForm, setFieldError }) => {
        const setFieldErrors = (fieldErrors: FieldError[]) => {
          fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
        };

        if (!userId) {
          const response = await createUser(values);
          const { created, fieldErrors, model, error } = response;

          if (fieldErrors) {
            setFieldErrors(fieldErrors);
            return;
          }

          if (!created || error) {
            errorToast(error || 'The user was not created');
            return;
          }

          resetForm({ values });
          toast('The user was created');
          setUserId(model?.id || '');
          handleChange(model as UserModel);
          setMode('view');
          return;
        }

        // ensure that sync groups are not removed from the user
        const syncGroups = user.groups.filter(
          (groupId) => !nonSyncGroupOpts?.some(({ value }) => value === groupId)
        );
        values.groups = [...syncGroups, ...values.groups];

        const { patched, error, fieldErrors } = await patchUser(
          user.id,
          values.propSlice(['name', 'email', 'groups'])
        );

        if (fieldErrors) {
          setFieldErrors(fieldErrors);
          return;
        }

        if (!patched || error) {
          errorToast(error || 'The user was not updated');
          return;
        }

        resetForm({ values });
        toast('The user was updated');
        handleChange(user);
        setMode('view');
      },
    },
    { lockTarget: 'drawer' }
  );
  const { handleSubmit, canSubmit, isSubmitting, loadData } = formHandle;

  const handleChange = (user?: UserModel) => {
    setUserId(user?.id || '');
    refetch();
    handleRefresh();
  };

  const handleCancel = () => {
    if (isNew) {
      onClose(true);
      return;
    }
    setMode('view');
    loadData(user || new UserModel());
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleDelete = () => {
    openModal('delete', {
      title: 'Delete user',
      content: `Are you sure you want to delete ${userName}?`,
      onClose: (confirm: boolean) => {
        if (confirm) {
          deleteUser(userId!).then(({ deleted, error }) => {
            if (deleted) {
              setUserId('');
              onClose(true);
              toast('The user was deleted');
              handleRefresh();
              handleChange();
              return;
            }

            errorToast(error || 'The user was not deleted');
          });
        }
      },
    });
  };

  const showUpdateMenu = !isNew && canChangeUsers;

  const deleteSubtitle = useMemo(() => {
    if (canDelete) {
      return '';
    }
    if (isExternal) {
      return 'You cannot delete a user that comes from sync';
    }
    if (isConnectorUser) {
      return 'Connector users are managed by the connector';
    }
    if (hasLogin) {
      return 'You cannot delete a user that has signed in';
    }
  }, [canDelete, isExternal, isConnectorUser, hasLogin]);

  const panels = [
    {
      label: 'User Profile',
      icon: 'info-circle',
      path: 'profile',
      component: <UserDetails user={user} groups={groups} policies={policies} />,
    },
    {
      label: 'Policies',
      icon: 'policy',
      path: 'policies',
      component: <UserPolicies groups={groups} policies={policies} />,
      disabled: isNew,
    },
    ...(isDashboardValid
      ? [
          {
            label: 'Insights',
            icon: 'bar-chart',
            path: 'insights',
            component: <UserInsights userEmail={user.email} />,
            disabled: isNew,
          },
        ]
      : []),
    {
      label: 'Timeline',
      icon: 'timeline',
      path: 'timeline',
      component: <UserTimeline userId={user.id} />,
    },
  ] as TabPanel[];

  return (
    <Drawer open={open} onClose={onClose} className={className} query={query} size="large">
      <DrawerHeader
        isTopDrawer={isTopDrawer}
        onClose={onClose}
        menu={
          <MoreMenu menuId="end-user-menu">
            {showUpdateMenu && !hideUpdateMenu && (
              <UpdateMenuItems
                onEdit={handleEdit}
                onDelete={handleDelete}
                canEdit={!inWriteMode}
                canDelete={canDelete && !inWriteMode}
                deleteSubtitle={deleteSubtitle}
              />
            )}
          </MoreMenu>
        }
      >
        {isNew ? 'Add User' : <UserLink user={user} size="large" action="none" showEmail={false} />}
      </DrawerHeader>
      <DrawerContent>
        {!inWriteMode ? (
          <RouterTabs variant="drawer" panels={panels} />
        ) : (
          <Form formHandle={formHandle} data={isNew ? undefined : user}>
            <InputControl
              name="name"
              label="Name"
              formHandle={formHandle}
              autoFocus
              disabled={!canEditProfile}
              required={isNew}
            />

            <InputControl
              name="email"
              label="Email"
              formHandle={formHandle}
              disabled={!canEditProfile}
            />

            <InputControl
              type="select-multi"
              name="groups"
              label="Workforce AI Security Groups"
              formHandle={formHandle}
              options={nonSyncGroupOpts}
            />

            <DrawerButtons
              submit
              cancel
              isNew={isNew}
              canSubmit={canSubmit}
              canCancel={!isSubmitting}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              formHandle={formHandle}
            />
          </Form>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default UserDrawer;
